/**
 * 外观类浮动面板（软件主题 / 键盘外观 / 未来新增的同类面板）互斥注册表。
 *
 * 设计目标：消除"打开 A 面板时手动调用 B.close()"的重复样板代码。
 *   - 任何外观面板模块在 `initialize` 时调用 `registerAppearancePanelCloser(id, closeFn)` 注册自己。
 *   - 其 `open*Panel()` 入口在打开自身前调用 `closeOtherAppearancePanels(id)`，由本表负责关掉其他兄弟面板。
 *
 * 这样：
 *   1. selector.vue / navigation.vue 等调用方不再需要知道"还有哪些同类面板需要顺手关闭"。
 *   2. 新增第三个外观面板时，只需在该模块注册即可，老调用方完全不动。
 */

type AppearancePanelCloser = () => void

const closers = new Map<string, AppearancePanelCloser>()
const openPanels = new Set<string>()

/**
 * 写一个共享的 `appearance-panel-open='true'` 标记到 documentElement / body 上。
 *
 * 该标记承担弹窗模糊冻结策略（参见 css/common.less 与 modal-performance.instructions.md）：
 * 任意外观浮动面板可见期间，背景主区的 `backdrop-filter` 都会被关闭，
 * 避免叠加多层全屏模糊导致的卡顿。
 *
 * 之前只有软件主题面板写了 `software-theme-panel-open`，键盘外观面板缺失对称属性，
 * 导致两个面板的性能体验不一致。统一收口到这里。
 */
const APPEARANCE_PANEL_OPEN_ATTR = 'appearance-panel-open'
const APPEARANCE_PANEL_SELECTOR = '[data-appearance-panel]'
const APPEARANCE_PANEL_TRIGGER_SELECTOR = '[data-appearance-panel-trigger]'

const isAppearancePanelEventTarget = (target: EventTarget | null): boolean => {
  return target instanceof Element && Boolean(target.closest(`${APPEARANCE_PANEL_SELECTOR}, ${APPEARANCE_PANEL_TRIGGER_SELECTOR}`))
}

const handleDocumentClick = (event: MouseEvent) => {
  if (openPanels.size === 0 || isAppearancePanelEventTarget(event.target)) {
    return
  }

  closeAllAppearancePanels()
}

const syncOutsideClickListener = () => {
  if (typeof document === 'undefined') {
    return
  }

  if (openPanels.size > 0) {
    document.addEventListener('click', handleDocumentClick, true)
    return
  }

  document.removeEventListener('click', handleDocumentClick, true)
}

const syncAppearancePanelOpenAttribute = () => {
  if (typeof document === 'undefined') {
    return
  }

  const value = openPanels.size > 0 ? 'true' : 'false'
  document.documentElement.setAttribute(APPEARANCE_PANEL_OPEN_ATTR, value)
  document.body.setAttribute(APPEARANCE_PANEL_OPEN_ATTR, value)
  syncOutsideClickListener()
}

export const registerAppearancePanelCloser = (id: string, close: AppearancePanelCloser) => {
  closers.set(id, close)
}

export const notifyAppearancePanelOpened = (id: string) => {
  openPanels.add(id)
  syncAppearancePanelOpenAttribute()
}

export const notifyAppearancePanelClosed = (id: string) => {
  openPanels.delete(id)
  syncAppearancePanelOpenAttribute()
}

export const closeOtherAppearancePanels = (excludeId: string) => {
  closers.forEach((close, id) => {
    if (id !== excludeId) {
      close()
    }
  })
}

export const closeAllAppearancePanels = () => {
  closers.forEach((close) => close())
}
